import { AsyncPipe, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import {
  CodecraftAnswer,
  CodecraftQuestion,
  CodecraftResult,
} from '@codecraft-works/data-models';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { QuizService } from '../data-access/quiz.service';
import { CodecraftQuizBooleanAnswerQuestionComponent } from '../ui/boolean-question.component';
import { CodecraftQuizMultipleAnswerQuestionComponent } from '../ui/multiple-question.component';
import { CodecraftQuizSingleAnswerQuestionComponent } from '../ui/single-question.component';

@Component({
  standalone: true,
  selector: 'codecraft-quiz-review-page-form',
  template: `
    <h2 class="col-12 mt-3 pb-2">{{ quizName$ | async }}</h2>
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <a href="#" (click)="backToTable(); $event.preventDefault()">
            Review Table
          </a>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          {{ quizName$ | async }}
        </li>
      </ol>
    </nav>

    <form *ngIf="questionForm && question" [formGroup]="questionForm">
      <ng-container [ngSwitch]="question.type">
        <ng-container *ngSwitchCase="'single'">
          <codecraft-quiz-single-answer-question
            [question]="question"
            [questionForm]="questionForm"
            [correctQuestionData]="correctQuestionData$ | async"
          ></codecraft-quiz-single-answer-question>
        </ng-container>

        <ng-container *ngSwitchCase="'boolean'">
          <codecraft-quiz-boolean-answer-question
            [question]="question"
            [questionForm]="questionForm"
            [correctQuestionData]="correctQuestionData$ | async"
            [answerGiven]="answerGiven"
          ></codecraft-quiz-boolean-answer-question>
        </ng-container>

        <ng-container *ngSwitchCase="'multiple'">
          <codecraft-quiz-multiple-answer-question
            [question]="question"
            [questionForm]="questionForm"
            [correctQuestionData]="correctQuestionData$ | async"
          ></codecraft-quiz-multiple-answer-question>
        </ng-container>
      </ng-container>
      <div class="btn-group mt-3" role="group">
        <button
          class="btn btn-secondary"
          type="button"
          [disabled]="currentQuestionIndex === 0"
          (click)="previousQuestion()"
        >
          Previous
        </button>
        <button
          class="btn btn-secondary"
          type="button"
          [disabled]="currentQuestionIndex === totalQuestions - 1"
          (click)="nextQuestion()"
        >
          Next
        </button>
      </div>
    </form>
  `,
  styles: [
    `
      nav {
        max-width: fit-content;
      }
    `,
  ],
  imports: [
    NgIf,
    NgSwitch,
    NgSwitchCase,
    AsyncPipe,
    ReactiveFormsModule,
    CodecraftQuizBooleanAnswerQuestionComponent,
    CodecraftQuizMultipleAnswerQuestionComponent,
    CodecraftQuizSingleAnswerQuestionComponent,
  ],
})
export class CodecraftQuizReviewPageFormComponent implements OnInit, OnChanges {
  @Input() question: CodecraftQuestion;
  @Input() questionResult: CodecraftResult;
  @Input() answerGiven: CodecraftAnswer;
  @Input() answer: CodecraftAnswer;
  @Input() isCorrect: boolean;
  @Input() currentQuestionIndex: number;
  @Input() totalQuestions: number;

  quizName$: Observable<string>;

  questionForm: FormGroup;
  correctQuestionData$: BehaviorSubject<{
    isCorrect: boolean;
    correctAnswer: CodecraftAnswer;
  } | null> = new BehaviorSubject(null);

  constructor(
    private quizService: QuizService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.quizName$ = this.quizService
      .getQuizInstance$()
      .pipe(map((quizInstance) => quizInstance?.name));
    if (!this.question) {
      return;
    }

    this.questionForm = this.createForm(this.question);
    if (this.questionForm) {
      this.questionForm.disable(); // Make the form read-only
    }
    this.correctQuestionData$.next({
      isCorrect: this.isCorrect,
      correctAnswer: this.answer,
    });

    if (!this.questionForm) {
      this.quizService.setQuizState({
        component: 'error',
        error: 'Invalid question form',
      });
      return;
    }
  }

  ngOnChanges(): void {
    if (this.question) {
      this.questionForm = this.createForm(this.question);
      if (this.questionForm) {
        this.questionForm.disable(); // Make the form read-only
      }
    }
    this.correctQuestionData$.next({
      isCorrect: this.isCorrect,
      correctAnswer: this.answer,
    });
  }

  createForm(question: CodecraftQuestion): FormGroup {
    let questionForm: FormGroup;
    if (question.type === 'boolean' || question.type === 'single') {
      questionForm = this.formBuilder.group({
        question: [this.answerGiven ?? '', Validators.required],
      });
    } else if (question.type === 'multiple') {
      const group: any = {};
      const checked =
        this.answerGiven && typeof this.answerGiven === 'object'
          ? this.answerGiven
          : {};

      // Handle both Map and Object structures
      if (question.choices instanceof Map) {
        question.choices.forEach((_value, key) => {
          group[key] = new FormControl(
            checked[key] ?? false,
            Validators.required
          );
        });
      } else if (question.choices && typeof question.choices === 'object') {
        // Handle regular object
        Object.keys(question.choices).forEach((key) => {
          group[key] = new FormControl(
            checked[key] ?? false,
            Validators.required
          );
        });
      }

      questionForm = this.formBuilder.group(group, {
        validators: requireCheckboxesToBeCheckedValidator(1),
      });
    }
    return questionForm;
  }

  nextQuestion() {
    this.correctQuestionData$.next(null);
    this.quizService.nextReviewQuestion();
  }

  backToTable() {
    this.correctQuestionData$.next(null);
    this.quizService.setQuizReviewState({
      component: 'review-table',
    });
  }

  previousQuestion() {
    this.correctQuestionData$.next(null);
    this.quizService.previousReviewQuestion();
  }
}

function requireCheckboxesToBeCheckedValidator(minRequired = 1): ValidatorFn {
  return function validate(formGroup: FormGroup) {
    let checked = 0;

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.controls[key];

      if (control.value === true) {
        checked++;
      }
    });

    if (checked < minRequired) {
      return {
        requireCheckboxesToBeChecked: true,
      };
    }

    return null;
  };
}
